<template>
  <div>
    <NotificationsSettingsModalWithPaidEligibleGreeting v-model:isVisible="isNotificationsSettingsAndGreetingModalVisible" />

    <div v-if="!dashboardData" class="centered-loader">
      <h2 class="loader-message">{{ loadingMessage }}</h2>
      <Loader style="margin-top: 10px"></Loader>
    </div>
    <!-- --- Onboarding - not yet paid eligible-->
    <template v-else-if="showV2CreatorOnboarding">
      <!-- Showing Custom Collabs if they have any while they're onboarding (this section is also below) -->
      <template v-if="allCustomCollabs?.length > 0">
        <!-- Collab tiles section -->
        <div class="collab-tiles-section" style="margin: 0 auto 45px">
          <h2 class="global-h2 collab-tiles-section__title">My Custom Collabs<BetaBadge class="standard-page-header__beta-badge" style="margin-left: 7px" /></h2>
          <div class="collab-tiles-section__subTitle">Work directly with destinations via a flexible collab system.</div>

          <ul class="collab-tiles-section__tiles-list">
            <CustomCollabActionSummaryTile
              class="collab-tiles-section__tile"
              v-for="customCollab in allCustomCollabs"
              :key="customCollab.customCollabId"
              :customCollabWidgetPayload="customCollab"
              userType="creator"
            />
          </ul>
        </div>
        <!-- / Collab tiles section -->
      </template>

      <CreatorOnboardingV2 style="margin: 0 auto" :dashboardSummary="dashboardData" />
    </template>

    <!-- --- Collab Eligible-->
    <div
      v-else
      class="main-wrapper"
      :style="{ width: ['desktop-wide', 'desktop', 'laptop'].includes(screenSize) ? 'var(--grid-col-right-width)' : 'var(--grid-content-full-width)', margin: '0 auto' }"
    >
      <div class="admin-dashboard">
        <div class="main-section">
          <NoteWithIcon v-if="getViewingUserProfile?.isFormerAdmin === true" color="yellow" style="margin-top: 15px">
            <template #icon><IconEmbedded name="info-simple_4" /></template>
            <template #text>
              <div>
                <h4 class="global-h4" style="margin-bottom: 3px">Your subscription has expired.</h4>
                Reach out to support@shrpa.com if you have any questions.
              </div>
            </template>
          </NoteWithIcon>

          <h1 class="global-h1">
            Hi <span v-if="getViewingUserProfile">{{ getViewingUserProfile.firstName }}</span>
          </h1>
          <p>
            Thanks for being part of Shrpa!<br />
            <span v-if="!getViewingUserProfile?.collabPreferences?.notInterestedInPaidCollabs && !getViewingUserProfile?.collabPreferences?.collabEligibleDisabled">
              Connect with over {{ approximateCustomerCount }} destinations across {{ totalCollabCount }} creator visits and counting!<br />
            </span>
          </p>
        </div>
      </div>

      <Loader v-if="dashboardDataIsLoading"></Loader>
      <div v-else>
        <ProfileDisabledWarning v-if="getViewingUserProfile?.disabledDate" style="margin-top: 15px" />

        <!--Collaborations-->
        <div class="collab-list" v-if="allCustomCollabs?.length > 0 || creatorCollabActionSummaryArrayNotCompleted?.length > 0 || getViewingUserProfile.collabPreferences.isCollabEligible">
          <div style="width: 100%; margin: 0 auto 40px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>

          <template v-if="creatorCollabActionSummaryArrayNotCompleted?.length > 0">
            <!-- Collab tiles section -->
            <div class="collab-tiles-section" style="margin: 0 auto 45px">
              <h2 class="global-h2 collab-tiles-section__title">My Shrpa Creator Visits</h2>
              <div class="collab-tiles-section__subTitle">Visit destinations via Shrpa's structured creator visits.</div>

              <ul class="collab-tiles-section__tiles-list">
                <CollabActionSummaryTile
                  class="collab-tiles-section__tile"
                  v-for="collabActionSummary in creatorCollabActionSummaryArrayNotCompleted"
                  :key="collabActionSummary.collabInputId"
                  :creatorCollabActionSummary="collabActionSummary"
                />
              </ul>
            </div>
            <!-- / Collab tiles section -->

            <div style="width: var(--grid-col-right-width); margin: 0 auto 40px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>
          </template>

          <template v-if="allCustomCollabs?.length > 0">
            <!-- Collab tiles section -->
            <div class="collab-tiles-section" style="margin: 0 auto 45px">
              <h2 class="global-h2 collab-tiles-section__title">My Custom Collabs<BetaBadge class="standard-page-header__beta-badge" style="margin-left: 7px" /></h2>
              <div class="collab-tiles-section__subTitle">Work directly with destinations via a flexible collab system.</div>

              <ul class="collab-tiles-section__tiles-list">
                <CustomCollabActionSummaryTile
                  class="collab-tiles-section__tile"
                  v-for="customCollab in allCustomCollabs"
                  :key="customCollab.customCollabId"
                  :customCollabWidgetPayload="customCollab"
                  userType="creator"
                />
              </ul>
            </div>
            <!-- / Collab tiles section -->

            <div style="width: var(--grid-col-right-width); margin: 0 auto 40px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>
          </template>
        </div>

        <!-- V2 Dashboard Available Collabs widget -->
        <template v-if="getViewingUserProfile.collabPreferences.isCollabEligible && getViewingUserProfile.collabPreferences.collabEligibleDisabled !== true">
          <AvailableCollabsList />

          <div style="width: 100%; margin: 50px auto 50px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>
        </template>

        <div style="display: flex; flex-direction: column; align-items: center">
          <div
            :style="{
              minWidth: screenSize === 'mobile' ? 'calc(100vw - 28px)' : 'auto',
              maxWidth: '600px',
            }"
          >
            <h2 class="global-h2" style="margin: 20px 0">What's happening on Shrpa</h2>
            <ActivityFeed :isWithH1="false" :allowShowMore="false" />
          </div>

          <div style="width: 100%; margin: 55px auto 45px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>

          <!-- Collab tiles section -->
          <div v-if="creatorCollabActionSummaryArrayCompleted?.length > 0" class="collab-tiles-section" style="margin: 0 auto 45px">
            <h2 class="global-h2 collab-tiles-section__title">Completed Shrpa Creator Visits</h2>
            <div class="collab-tiles-section__subTitle">Your completed visits, great job!</div>

            <ul class="collab-tiles-section__tiles-list">
              <CollabActionSummaryTile
                class="collab-tiles-section__tile"
                v-for="collabActionSummary in creatorCollabActionSummaryArrayCompleted"
                :key="collabActionSummary.collabInputId"
                :creatorCollabActionSummary="collabActionSummary"
              />
            </ul>
          </div>
          <!-- / Collab tiles section -->
        </div>
      </div>

      <!--Footer-->
      <div class="dashboardFooter">Thanks for being part of Shrpa! <i class="red heart icon"></i></div>
    </div>

    <!--Modals-->

    <!--Ask if Interested in Paid Creation Modal-->
    <NewCreatorOptInModal
      v-if="getViewingUserProfile?.collabPreferences?.isCollabEligible === false"
      v-model:isVisible="showAskIfInterestedInPaidCreationModal"
      :loading="isSavingCreatorFields"
      @click:notInterested="notInterestedInPaidCreation"
      @click:interestedWithLocation="saveInterestedInPaidCreation"
    />

    <!--Missing Email Modal-->
    <SrpModal v-model:isVisible="showMissingEmailModal" :isClosable="false" :isCloseButtonVisible="false">
      <template #header>
        <h2 class="global-h2">Looks like we don't have your email</h2>
      </template>
      <template #content>
        Email is needed to receive important notifications for collaborations and other updates!<br />
        <b>Email:</b><input type="email" v-model="missingEmailAddress" placeholder="person@domain.com" style="max-width: 500px; display: inline; margin-left: 8px; margin-top: 5px" />
        <br />
        <b v-if="missingEmailValidation" style="color: red">{{ missingEmailValidation }}</b>
      </template>
      <template #footer>
        <SrpButton :isDisabled="savingMissingEmail" class="ui primary button" @click="saveMissingEmail()">Submit</SrpButton>
      </template>
    </SrpModal>
  </div>
</template>

<script lang="ts">
import axios from "axios";
import DateUtils from "@logic/DateUtils";
import { defineComponent, inject } from "vue";
import { mapActions, mapState } from "pinia";
import moment from "moment";
import { RouteHelper } from "@helpers/RouteHelper";
import { useHead } from "@unhead/vue";
import { MetricSender } from "@/helpers/MetricSender";
import { CreatorRedirectHelper } from "@/logic/CreatorRedirectHelper";
import CreatorCustomCollabsRepo from "@repos/CreatorCustomCollabsRepo";

// Types
import { CollabInput, CreatorCollab } from "@contracts/collab";
import { CreatorDashboardSummary } from "@contracts/creatorPublicProfileFields";
import { NewCreatorPaidInterestedModel } from "@components/NewCreatorOptInModal.vue";
import { ScreenSize } from "@contracts/screenSize";

// Stores
import { useUserProfileStore } from "@stores/userProfileStore";

// Components
import ActivityFeed from "@components/ActivityFeed/index.vue";
import AvailableCollabsList from "./AvailableCollabsList.vue";
import CollabActionSummaryTile from "@components/collabSnippets/CollabActionSummaryTile.vue";
import CopyText from "@components/CopyText.vue";
import CreatorOnboardingV2 from "@views/Creator/CreatorOnboardingV2/index.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import Loader from "@components/Loader/Loader.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import NewCreatorOptInModal from "@components/NewCreatorOptInModal.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import PaidCreatorFields from "@components/PaidCreatorFields.vue";
import PaidCreatorFieldsEnteredMessage from "@components/PaidCreatorFieldsEnteredMessage.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import { CreatorCollabActionSummary } from "@contracts/collabActionSummaries";
import NotificationsSettingsModalWithPaidEligibleGreeting from "@components/NotificationsSettingsModalWithPaidEligibleGreeting.vue";
import ProfileDisabledWarning from "@components/Sherpa/ProfileDisabledWarning.vue";
import { CreatorInviteKey } from "@/logic/CreatorInviteKey";
import CustomCollabActionSummaryTile from "@views/CustomCollabs/CustomerCustomCollabs/CustomCollabActionSummaryTile.vue";
import { CustomCollabWidgetPayload } from "@contracts/customCollabs";
import BetaBadge from "@components/BetaBadge.vue";

// Note: This is now ONLY authenticated, see UnauthenticatedCreator.vue for the unauthenticated flow

export default defineComponent({
  name: "CreatorDashboard",

  // prettier-ignore
  components: { CustomCollabActionSummaryTile, NotificationsSettingsModalWithPaidEligibleGreeting, ActivityFeed, AvailableCollabsList, CollabActionSummaryTile, CopyText, CreatorOnboardingV2, IconEmbedded, Loader, LinkWithIcon, NewCreatorOptInModal, NoteWithIcon, PaidCreatorFields, PaidCreatorFieldsEnteredMessage, SrpButton, SrpModal, ProfileDisabledWarning, BetaBadge },

  data() {
    return {
      globalLog: inject("globalLog") as any,
      globalRemoteLogger: inject("globalRemoteLogger") as any,
      globalGetLink: inject("globalGetLink") as any,
      screenSize: inject("screenSize") as ScreenSize,

      // @ts-ignore
      contentBaseUri: globalThis.Bootstrap.Config.contentCdnBaseUri as string,

      isCreatorCollabActionSummaryArrayLoading: false,
      creatorCollabActionSummaryArray: [] as Array<CreatorCollabActionSummary>,
      allCustomCollabs: [] as Array<CustomCollabWidgetPayload>,

      dashboardDataIsLoading: true,
      loadingMessage: "Loading...",
      dashboardData: null as CreatorDashboardSummary | null,
      topLevelStatsToShowCreators: null,

      // Paid Creator Fields
      showAskIfInterestedInPaidCreationModal: false,
      isSavingCreatorFields: false,

      // Missing email
      showMissingEmailModal: false,
      missingEmailAddress: null,
      missingEmailValidation: null,
      savingMissingEmail: false,

      isNotificationsSettingsAndGreetingModalVisible: false,
    };
  },

  computed: {
    ...mapState(useUserProfileStore, ["getViewingUserProfile", "getActingUserProfile", "isSuperOrSalesUser", "getViewingCreatorIdIfImpersonating"]),
    creatorCollabActionSummaryArrayNotCompleted(): Array<CreatorCollabActionSummary> {
      return this.creatorCollabActionSummaryArray.filter(s => s.completedDate === null);
    },
    creatorCollabActionSummaryArrayCompleted(): Array<CreatorCollabActionSummary> {
      return this.creatorCollabActionSummaryArray.filter(s => s.completedDate !== null);
    },
    showV2CreatorOnboarding(): boolean {
      if (
        this.getViewingUserProfile?.collabPreferences?.isCollabEligible === true ||
        this.getViewingUserProfile?.collabPreferences?.notInterestedInPaidCollabs === true ||
        this.getViewingUserProfile?.collabPreferences?.collabEligibleDisabled === true
      )
        return false;
      return this.getViewingUserProfile?.collabPreferences?.becomingCollabEligible === true || this.dashboardData?.promptToBePaidCreator === true;
    },

    approximateCustomerCount(): number {
      if (this.topLevelStatsToShowCreators) {
        return this.topLevelStatsToShowCreators.approximateDmoCustomerCount;
      }
      return 200;
    },
    totalCollabCount(): number {
      if (this.topLevelStatsToShowCreators) {
        return this.topLevelStatsToShowCreators.totalCollabsSoldCount;
      }
      return 1000;
    },
  },

  // UPDATE: This page is only authenticated.  Login.vue handles the unauthenticated flow

  async mounted() {
    await this.loadAllData();
    await this.getTopLevelStatsToShowCreators();

    useHead({ title: () => `Dashboard - ${this.getViewingUserProfile?.firstName}` });

    if (this.$route.query?.justBecamePaidEligible === "true") {
      this.isNotificationsSettingsAndGreetingModalVisible = true;
      MetricSender.setPropertyOnSession("OnboardingState", "Complete");
    }

    this.allCustomCollabs = await CreatorCustomCollabsRepo.getAllCustomCollabsV2(String(this.$route.params.creatorId), this.getViewingCreatorIdIfImpersonating);
  },

  methods: {
    moment,
    ...mapActions(useUserProfileStore, ["impersonateProfile", "saveProfile"]),
    async getCollabsSummaryWithActions() {
      this.isCreatorCollabActionSummaryArrayLoading = true;

      // DON'T USE params.creatorId, that's optional and only for supers
      let uri = `${import.meta.env.VITE_API_URL}/collab-summary/creators/${this.getViewingUserProfile.sherpaId}/summary-with-actions`;

      // Add impersonatedUserCreatorId to query params if it exists
      if (this.getViewingCreatorIdIfImpersonating) {
        const queryParams = new URLSearchParams();
        queryParams.append("impersonatedUserCreatorId", this.getViewingCreatorIdIfImpersonating);
        uri += `?${queryParams.toString()}`;
      }

      const { data } = await axios.get(uri);
      this.creatorCollabActionSummaryArray = data;

      this.isCreatorCollabActionSummaryArrayLoading = false;
    },

    async loadAllData() {
      this.globalLog.info(`CreatorDashboard loadAllData`);
      var creatorIdOverride = this.$route.params.creatorId as string;
      this.globalLog.info(`CreatorDashboard creatorIdParam=${creatorIdOverride}`);
      // Default the profile details
      await this.setViewingProfile(creatorIdOverride);

      // Check for redirects to sub-pages
      this.checkForRedirect();
      // Check for an invite key
      await this.checkForInviteKey();

      this.checkForMissingEmail();
      this.getCollabsSummaryWithActions();
      this.loadDashboardData();
    },
    async setViewingProfile(creatorIdOverride: string | null) {
      this.globalLog.info(`CreatorDashboard setting view Profile`);
      // Check if we're an admin loading someone else's profile
      if (creatorIdOverride) {
        await this.impersonateProfile(creatorIdOverride);
      }
    },
    async getTopLevelStatsToShowCreators() {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/creators/stats/overall`);
      this.topLevelStatsToShowCreators = response.data;
    },
    // ---Dashboard---
    async loadDashboardData() {
      this.globalLog.info("Loading Dashboard Data");
      var currentCreatorId = this.getViewingUserProfile.sherpaId;
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/creators/${currentCreatorId}/dashboard`);
      this.dashboardData = response.data;

      // Paid Creator Flow
      this.checkPaidCreatorFlow(this.dashboardData);

      this.dashboardDataIsLoading = false;
    },

    checkForRedirect() {
      if (CreatorRedirectHelper.checkForRedirect(this.$router, this.getViewingUserProfile.sherpaId)) {
        this.loadingMessage = "Redirecting...";
      }
    },
    async checkForInviteKey() {
      if (CreatorInviteKey.isInviteKeyPresent()) {
        this.loadingMessage = "Processing invite...";
        this.globalRemoteLogger.info(`CreatorDashboard: InviteKey Found! Processing for ${this.getViewingUserProfile.sherpaId}, inviteKey=${CreatorInviteKey.getInviteKey()}`);

        // Process the invite key
        var result = await CreatorInviteKey.processInviteKey(this.getViewingUserProfile.sherpaId);

        // Log the result
        if (result !== false) {
          this.globalRemoteLogger.info(
            `CreatorDashboard: InviteKey Redirecting for ${this.getViewingUserProfile.sherpaId}: ${result.status}, ${result.customerId} customCollabId=${result.customCollabId}`
          );
          this.$router.push({
            name: "CreatorCustomCollabDetails",
            params: {
              creatorId: this.getViewingUserProfile.sherpaId,
              communityId: result.customerId,
              customCollabId: result.customCollabId,
            },
          });
        } else {
          this.globalRemoteLogger.info(`CreatorDashboard: InviteKey NoAction for ${this.getViewingUserProfile.sherpaId}`);
        }
      }
    },

    async checkPaidCreatorFlow(dashboardData: CreatorDashboardSummary) {
      if (
        dashboardData.promptToBePaidCreator &&
        this.getViewingUserProfile.collabPreferences.notInterestedInPaidCollabs !== true &&
        this.getViewingUserProfile.collabPreferences.becomingCollabEligible !== true &&
        // Don't ask superusers if they're viewing their own profile
        this.getViewingUserProfile.isSuperuser !== true &&
        this.getViewingUserProfile.isSalesUser !== true
      ) {
        // UPDATE: Changed this to be a modal that we have the user answer.
        this.showAskIfInterestedInPaidCreationModal = true;
      }
    },

    // --Paid Collab Interest responses--
    async notInterestedInPaidCreation(): Promise<void> {
      this.globalRemoteLogger.info(`Dashboard: NOT interested in paid collabs: ${this.getViewingUserProfile?.authNameId}`);
      this.isSavingCreatorFields = true;
      this.getViewingUserProfile.collabPreferences.notInterestedInPaidCollabs = true;
      // Save the profile
      this.showAskIfInterestedInPaidCreationModal = false;
      await this.saveProfile(this.getViewingUserProfile);
      this.isSavingCreatorFields = false;
      this.showAskIfInterestedInPaidCreationModal = false;
    },
    async saveInterestedInPaidCreation(paidInterestedFields: NewCreatorPaidInterestedModel) {
      this.globalRemoteLogger.info(`Dashboard: IS interested in paid collabs: ${this.getViewingUserProfile?.sherpaId}`, false, false, {
        heardAbout: paidInterestedFields.heardAbout,
        referredByCode: paidInterestedFields.referredByCode,
      });
      this.isSavingCreatorFields = true;
      const profile = this.getViewingUserProfile;
      // use updated collab preferences which includes location data, and force becomingCollabEligible to true
      profile.collabPreferences = { ...paidInterestedFields.updatedCollabPreferences, becomingCollabEligible: true };
      profile.heardAbout = paidInterestedFields.heardAbout;
      profile.heardAboutOther = paidInterestedFields.heardAboutOther;
      await this.saveProfile(profile, paidInterestedFields.referredByCode);
      this.isSavingCreatorFields = false;
      this.showAskIfInterestedInPaidCreationModal = false;

      MetricSender.setPropertyOnSession("Creator-Onboarding", true);
    },

    // ---Collab Methods---
    declinedCollab(collab: CreatorCollab) {
      if (collab.creatorInput.declinedDateTime) return true;
      return false;
    },
    differentCreatorSelected(collab: CreatorCollab) {
      var usersSherpaId = this.getViewingUserProfile.sherpaId;
      return collab.collabInput.collaboration.creatorId && collab.collabInput.collaboration.creatorId !== usersSherpaId;
    },
    formatPreferredTimingDates(collaborationInput: CollabInput) {
      return DateUtils.formatDateRangeTwoDates(collaborationInput.preferredDateRangeStart, collaborationInput.preferredDateRangeEnd);
    },

    checkForMissingEmail() {
      // Checks if we should prompt the user with the missing email modal/flow
      let parameter = RouteHelper.getQueryStringParam("missing-email-test");
      if (parameter?.toLowerCase() === "true") {
        // Just for testing
        this.showMissingEmailModal = true;
      } else {
        // Normal flow, ensure we only show this if they don't have a valid email
        let currentEmail = this.getViewingUserProfile.email;
        if (!currentEmail || currentEmail.trim().length === 0 || currentEmail === "undefined") this.showMissingEmailModal = true;
      }
    },
    async saveMissingEmail() {
      if (!this.missingEmailAddress || this.missingEmailAddress?.trim().length < 5 || !this.missingEmailAddress.includes("@")) {
        this.missingEmailValidation = "Please enter a valid email address";
        return;
      }
      this.missingEmailValidation = null;
      this.savingMissingEmail = true;
      // Note: This doesn't update the profile eTag so may cause a false positive there, should improve this at some point (it's low volume though)
      var uri = `${import.meta.env.VITE_API_URL}/profile/${this.getViewingUserProfile.authNameId}/email?newEmail=${encodeURIComponent(this.missingEmailAddress)}`;
      var updated = await axios.put(uri);
      this.globalLog.info(`saveMissingEmail ${this.missingEmailAddress}: wasUpdated=${updated.data}`);

      this.getViewingUserProfile.email = this.missingEmailAddress;
      this.showMissingEmailModal = false;
      this.savingMissingEmail = false;
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";
@import "@/scss/screen-size-ranges.scss";

// Collab tiles section =======================================================
.collab-tiles-section {
  width: $desktop-wide-right-col-width;

  &__title {
    margin-bottom: 5px;
  }

  &__subTitle {
    margin-bottom: 15px;
  }

  &__tiles-list {
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: $desktop-wide-grid-gap-width;
    list-style: none;
  }

  &__tile {
    width: calc(50% - $desktop-wide-grid-gap-width / 2 - 1px);
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .collab-tiles-section {
    width: $desktop-right-col-width;

    &__tiles-list {
      gap: $desktop-grid-gap-width;
    }

    &__tile {
      width: calc(50% - $desktop-grid-gap-width / 2 - 1px);
    }
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .collab-tiles-section {
    width: $laptop-right-col-width;

    &__tiles-list {
      gap: $laptop-grid-gap-width;
    }

    &__tile {
      width: calc(50% - $laptop-grid-gap-width / 2 - 1px);
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .collab-tiles-section {
    width: $tablet-large-content-width;
    min-width: $tablet-large-content-width;

    &__tiles-list {
      gap: $tablet-large-grid-gap-width;
    }

    &__tile {
      width: calc(50% - $tablet-large-grid-gap-width / 2 - 1px);
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .collab-tiles-section {
    width: $tablet-content-width;

    &__tiles-list {
      gap: $tablet-grid-gap-width;
    }

    &__tile {
      width: 100%;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .collab-tiles-section {
    width: $mobile-content-width;

    &__tiles-list {
      gap: $mobile-grid-gap-width;
    }

    &__tile {
      width: 100%;
    }
  }
}

.create-adventure-button {
  font-weight: bold;
  color: $brand-color;
  cursor: pointer;
}

.dashboardFooter {
  text-align: center;
  margin-top: 100px;
  width: 100%;
}

.collab-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 3em;

  .collab-section {
    width: 100%;
  }

  .collab-subheading {
    margin-bottom: 5px;
  }

  .collab-section-description {
    margin-bottom: 10px;
  }

  .totals {
    //border-bottom: 1px solid #ccc;
    font-size: 1.2rem;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    width: 100%;

    &:not(:first-of-type) {
      //border-top: 1px solid #ccc;
      margin-top: 1rem;
      padding-top: 1rem;
    }

    label {
      font-size: 1.5rem;
      margin-top: 0.5rem !important;
      padding: 1rem;
    }
  }

  .collab {
    box-shadow: 0 0 5px 0 #ccc;
    display: flex;
    flex-direction: column;
    margin-bottom: 1.5rem;
    padding: 1rem;
    position: relative;
    width: 100%;

    @media screen and (min-width: 600px) {
      width: 47%;
    }

    > div {
      &.community-name {
        max-width: 250px;
        font-size: 1.4rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
      }

      &:last-of-type {
        margin-bottom: 1rem;
      }
    }

    .collab-name {
      max-width: 250px;
    }

    .collab-timing {
      max-width: 220px;
    }

    .partner-img {
      position: absolute;
      right: 1rem;
      top: 1rem;
      max-height: 90px;
      max-width: 120px;
    }

    .another,
    .accepted,
    .decline {
      border-radius: 0.5rem;
      padding: 0.5rem 1rem;
    }

    .another {
      background-color: rgba(236, 86, 59, 0.2);
    }

    .decline {
      background-color: rgba(204, 204, 204, 0.4);
    }

    .accepted {
      background: rgba(179, 222, 222, 0.5);
    }

    .button {
      margin-top: auto;
      max-width: 250px;
      width: 100%;
    }
  }
}

.centered-loader {
  position: fixed;
  top: 20%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loader-message {
  text-align: center;
  /*font-size: 1.2rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  margin-bottom: 0.5rem;*/
}
</style>
